import { locationChangeAction } from 'store/middlewares/adobe-analytics-middleware';

/**
 * Navigation utility for React Router v6
 * This provides navigation functionality that can be used in Redux actions
 * and other parts of the application where direct access to useNavigate is not available
 */
const NAVIGATION_NOT_INITIALIZED_MSG =
  'Navigation not initialized. Call initializeNavigation first.';
let navigate;
let dispatch;
let navigationBlocker = null;

/**
 * Initialize the navigation utility with the navigate function and Redux dispatch
 * This should be called once when the app starts
 */
export const initializeNavigation = (
  navigateFunction,
  dispatchFunction = null,
) => {
  navigate = navigateFunction;
  if (dispatchFunction) {
    dispatch = dispatchFunction;
  }
};

/**
 * Set a navigation blocker function
 * @param {function} blocker - Function that returns true to allow navigation, false to block
 */
export const setNavigationBlocker = (blocker) => {
  navigationBlocker = blocker;
};

/**
 * Clear the navigation blocker
 */
export const clearNavigationBlocker = () => {
  navigationBlocker = null;
};

/**
 * Navigate to a specific path
 * @param {string} path - The path to navigate to
 * @param {object} options - Navigation options (replace, state, etc.)
 */
export const navigateTo = (path, options = {}) => {
  // Check if navigation is blocked
  if (navigationBlocker && !navigationBlocker(path)) {
    return false;
  }

  if (navigate) {
    navigate(path, options);
    // Dispatch location change for Adobe Analytics
    if (dispatch) {
      dispatch(locationChangeAction(path));
    }
  } else {
    console.warn(NAVIGATION_NOT_INITIALIZED_MSG);
  }
};

/**
 * Navigate back in history
 */
export const navigateBack = () => {
  if (navigate) {
    navigate(-1);
  } else {
    console.warn(NAVIGATION_NOT_INITIALIZED_MSG);
  }
};

/**
 * Navigate forward in history
 */
export const navigateForward = () => {
  if (navigate) {
    navigate(1);
  } else {
    console.warn(NAVIGATION_NOT_INITIALIZED_MSG);
  }
};

/**
 * Replace the current entry in history
 * @param {string} path - The path to navigate to
 * @param {object} options - Navigation options (state, etc.)
 */
export const replaceTo = (path, options = {}) => {
  if (navigate) {
    navigate(path, { replace: true, ...options });
  } else {
    console.warn(NAVIGATION_NOT_INITIALIZED_MSG);
  }
};
